// src/mobile_pages/meeting/MobileScheduleDetail.jsx
// 모바일 전용 일정 상세 컴포넌트 (닫기 버튼 포함)

import React from "react";
import { format } from "date-fns";
import { scheduleApi } from "../../api/schedules";

// 아이콘 컴포넌트
const ClockIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z" />
    </svg>
);

const UsersIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
);

const MemoIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
);

const CloseIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
);

export default function MobileScheduleDetail({ item, onEdit, onDeleted, onClose }) {
    if (!item) return null;

    const dt = item.start
        ? format(new Date(item.start), "yyyy-MM-dd HH:mm")
        : "-";

    const endDt = item.end
        ? format(new Date(item.end), "yyyy-MM-dd HH:mm")
        : null;

    const scopeConfig = {
        personal: {
            bg: "bg-emerald-50",
            text: "text-emerald-600",
            border: "border-emerald-200",
            label: "개인"
        },
        company: {
            bg: "bg-rose-50",
            text: "text-rose-600",
            border: "border-rose-200",
            label: "회사"
        },
        project: {
            bg: "bg-blue-50",
            text: "text-blue-600",
            border: "border-blue-200",
            label: "프로젝트"
        },
    };

    const scope = scopeConfig[item.scope] || scopeConfig.project;

    const handleDelete = async () => {
        const ok = window.confirm("정말 삭제하시겠습니까?");
        if (!ok) return;
        await scheduleApi.remove(item.id);
        onDeleted?.();
    };

    return (
        <div className="space-y-5">

            {/* ============================
          HEADER
      ============================ */}
            <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2.5 mb-2">
                        <span
                            className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full border ${scope.bg} ${scope.text} ${scope.border}`}
                        >
                            {scope.label}
                        </span>
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 leading-tight">
                        {item.title}
                    </h3>
                    <p className="text-sm text-slate-400 mt-1">
                        일정 상세 정보
                    </p>
                </div>

                {/* 닫기 버튼 - 항상 표시 */}
                <button
                    onClick={onClose}
                    className="p-2.5 -mr-2 -mt-1 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 active:bg-slate-200 transition-all duration-200"
                    title="닫기"
                >
                    <CloseIcon />
                </button>
            </div>

            {/* ============================
          INFO CARDS
      ============================ */}
            <div className="space-y-3">

                {/* 일시 카드 */}
                <div className="rounded-2xl bg-slate-50/80 border border-slate-100 p-4 hover:bg-slate-50 transition-colors duration-200">
                    <div className="flex items-center gap-2 text-slate-500 mb-2">
                        <ClockIcon />
                        <span className="text-sm font-medium">일시</span>
                    </div>
                    <div className="text-slate-800 text-sm font-medium">
                        {dt}
                        {endDt && (
                            <span className="text-slate-400 mx-2">→</span>
                        )}
                        {endDt && <span>{endDt}</span>}
                    </div>
                </div>

                {/* 참여자 카드 — personal 일정에서는 숨김 */}
                {item.scope !== "personal" && (
                    <div className="rounded-2xl bg-slate-50/80 border border-slate-100 p-4 hover:bg-slate-50 transition-colors duration-200">
                        <div className="flex items-center gap-2 text-slate-500 mb-2">
                            <UsersIcon />
                            <span className="text-sm font-medium">참여자</span>
                        </div>
                        {item.participants?.length ? (
                            <div className="flex gap-2 flex-wrap">
                                {item.participants.map((p) => (
                                    <span
                                        key={p.id}
                                        className="inline-flex items-center px-3 py-1.5 rounded-full bg-white border border-slate-200 text-sm text-slate-700 shadow-sm"
                                    >
                                        {p.name}
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <p className="text-slate-400 text-sm">
                                참여자가 없습니다.
                            </p>
                        )}
                    </div>
                )}

                {/* 메모 카드 */}
                <div className="rounded-2xl bg-slate-50/80 border border-slate-100 p-4 hover:bg-slate-50 transition-colors duration-200">
                    <div className="flex items-center gap-2 text-slate-500 mb-2">
                        <MemoIcon />
                        <span className="text-sm font-medium">메모</span>
                    </div>
                    {item.memo ? (
                        <p className="text-slate-700 text-sm whitespace-pre-wrap leading-relaxed">
                            {item.memo}
                        </p>
                    ) : (
                        <p className="text-slate-400 text-sm">
                            등록된 메모가 없습니다.
                        </p>
                    )}
                </div>
            </div>

            {/* ============================
          ACTION BUTTONS
      ============================ */}
            <div className="flex items-center gap-3 pt-4">
                {/* 수정 버튼 */}
                <button
                    className="flex-1 px-4 py-3 rounded-2xl bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold text-sm hover:from-blue-600 hover:to-blue-700 active:scale-[0.98] shadow-lg shadow-blue-500/25 transition-all duration-200"
                    onClick={() => onEdit(item)}
                >
                    수정
                </button>

                {/* 삭제 버튼 */}
                <button
                    className="flex-1 px-4 py-3 rounded-2xl border-2 border-red-200 text-red-500 font-semibold text-sm hover:bg-red-50 hover:border-red-300 active:bg-red-100 transition-all duration-200"
                    onClick={handleDelete}
                >
                    삭제
                </button>
            </div>
        </div>
    );
}
